import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Grid, Card, CardContent, Chip, Button, CircularProgress, Alert,
  Divider, IconButton, Tooltip as MuiTooltip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tabs, Tab
} from '@mui/material';
import {
  Close, TrendingUp, TrendingDown, Timeline, Security,
  ArrowUpward, CheckCircle, BarChart as BarChartIcon
} from '@mui/icons-material';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Cell
} from 'recharts';
import dayjs from '../../utils/dayjs';
import { trading } from '../../services/api';

const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `₹${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Custom Dot to highlight Trailing SL triggers on the step line
const TrailingDot = (props) => {
  const { cx, cy, payload } = props;
  if (!payload || !payload.isTrailing) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={6} fill="#f59e0b" stroke="#ffffff" strokeWidth={2} />
      <circle cx={cx} cy={cy} r={9} fill="none" stroke="#f59e0b" strokeWidth={1.5} opacity={0.7} />
    </g>
  );
};

// Custom Tooltip for Chart Columns & Trailing Steps
const CustomTooltip = ({ active, payload, label, buyPrice }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  const isProfit = data.price >= buyPrice;
  const pnlColor = isProfit ? '#10b981' : '#ef4444';

  return (
    <Box
      sx={{
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        p: 1.5,
        minWidth: 220,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8, pb: 0.6, borderBottom: '1px solid #f1f5f9' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
          ⏱ {data.timeLabel || label}
        </Typography>
        {data.isEntry && <Chip label="PURCHASE / ENTRY" size="small" color="primary" sx={{ height: 18, fontSize: 9, fontWeight: 800 }} />}
        {data.isExit && <Chip label="SOLD / EXIT" size="small" color="default" sx={{ height: 18, fontSize: 9, fontWeight: 800 }} />}
        {data.isOpenLive && <Chip label="LIVE PRICE" size="small" color="success" sx={{ height: 18, fontSize: 9, fontWeight: 800 }} />}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">Price:</Typography>
          <Typography variant="body2" fontWeight={700}>{fmtMoney(data.price)}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">Trailing SL:</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: '#dc2626' }}>
            {fmtMoney(data.sl)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">P&L:</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: pnlColor }}>
            {data.pnl >= 0 ? '+' : ''}{fmtMoney(data.pnl)} ({data.pnlPct >= 0 ? '+' : ''}{data.pnlPct}%)
          </Typography>
        </Box>

        {data.isTrailing && (
          <Box
            sx={{
              mt: 1,
              p: 0.8,
              bgcolor: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ArrowUpward sx={{ fontSize: 13, color: '#b45309' }} />
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#b45309', fontSize: 11 }}>
                TRAILING SL TRIGGERED
              </Typography>
            </Box>
            {data.trailEvent && (
              <Typography variant="caption" sx={{ color: '#92400e', fontSize: 10 }}>
                SL ratcheted to {fmtMoney(data.trailEvent.newSL || data.trailEvent.new_sl)} ({data.trailEvent.reason || 'Profit Locked'})
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default function TradeLifecycleChartDialog({ open, orderId, tradeId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!open || (!orderId && !tradeId)) return;
    setLoading(true);
    setError(null);

    const req = orderId
      ? trading.equityOrderLifecycleChart(orderId)
      : trading.tradeLifecycleChart(tradeId);

    req
      .then(res => {
        if (res.data?.success && res.data?.data) {
          setData(res.data.data);
        } else {
          setError(res.data?.message || 'Could not load lifecycle chart');
        }
      })
      .catch(err => {
        setError(err.response?.data?.message || err.message || 'Failed to fetch lifecycle chart');
      })
      .finally(() => setLoading(false));
  }, [open, orderId, tradeId]);

  if (!open) return null;

  const order = data?.order;
  const summary = data?.summary;
  const points = data?.points || [];
  const trailingEvents = data?.trailingEvents || [];

  const buyPrice = order?.buyPrice || 0;
  const exitPrice = order?.sellPrice || order?.currentPrice || 0;
  const isProfit = summary?.isProfit ?? ((exitPrice - buyPrice) >= 0);

  const allPrices = points.flatMap(p => [p.price, p.sl]).filter(v => v != null && !isNaN(v) && v > 0);
  const minVal = allPrices.length ? Math.min(...allPrices, buyPrice) : 0;
  const maxVal = allPrices.length ? Math.max(...allPrices, buyPrice) : 100;
  const padding = Math.max(1, (maxVal - minVal) * 0.08);
  const yDomain = [Math.floor(minVal - padding), Math.ceil(maxVal + padding)];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}>
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <BarChartIcon sx={{ color: '#3b82f6', fontSize: 26 }} />
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
              {order ? `${order.symbol} — Trade Lifecycle` : 'Trade Lifecycle Graph'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Purchase to Sell Trajectory with Trailing Stop-Loss Milestones
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        {loading ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Loading trade trajectory and trailing history...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : !data || !points.length ? (
          <Alert severity="info" sx={{ my: 2 }}>No trade history data points available.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Top KPI Banner */}
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">Purchased At</Typography>
                    <Typography variant="subtitle1" fontWeight={800}>
                      {fmtMoney(order.buyPrice)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {dayjs(order.placedAt).format('DD MMM, HH:mm')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      {order.status === 'SOLD' ? 'Sold At' : 'Current Price'}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: isProfit ? '#15803d' : '#b91c1c' }}>
                      {fmtMoney(exitPrice)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {order.soldAt ? dayjs(order.soldAt).format('DD MMM, HH:mm') : '● Live Position'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: isProfit ? '#f0fdf4' : '#fef2f2' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" sx={{ color: isProfit ? '#15803d' : '#b91c1c', fontWeight: 700 }}>
                      Net P&L
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: isProfit ? '#15803d' : '#b91c1c' }}>
                      {summary.finalPnl >= 0 ? '+' : ''}{fmtMoney(summary.finalPnl)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isProfit ? '#15803d' : '#b91c1c', fontWeight: 600, fontSize: '0.7rem' }}>
                      ({summary.finalPnlPct >= 0 ? '+' : ''}{summary.finalPnlPct}%)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#fffbeb' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 700 }}>
                        Trailing SL
                      </Typography>
                      {summary.trailingEventsCount > 0 && (
                        <Chip
                          label={`${summary.trailingEventsCount} Trails`}
                          size="small"
                          sx={{ height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#b45309', fontWeight: 800 }}
                        />
                      )}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#b45309' }}>
                      {fmtMoney(order.currentSL)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Initial: {fmtMoney(order.initialSL)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* View Mode Tabs (Graph vs Trailing Events Table) */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ minHeight: 36 }}>
                <Tab label="📊 Column & Trailing Graph" sx={{ minHeight: 36, py: 0.5, fontWeight: 700, textTransform: 'none' }} />
                <Tab label={`⚡ Trailing Milestones (${trailingEvents.length})`} sx={{ minHeight: 36, py: 0.5, fontWeight: 700, textTransform: 'none' }} />
              </Tabs>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip size="small" label={`⏱ Held: ${summary.durationLabel}`} sx={{ fontWeight: 600, fontSize: 11 }} />
                <Chip size="small" label={`Max Peak: +${summary.maxGainPct}%`} sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 700, fontSize: 11 }} />
              </Box>
            </Box>

            {/* Tab 0: Chart View */}
            {tabIndex === 0 && (
              <Box>
                {/* Visual Legend */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5, flexWrap: 'wrap', px: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#10b981', borderRadius: 0.5 }} />
                    <Typography variant="caption" color="text.secondary">Profit Price Bar</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#ef4444', borderRadius: 0.5 }} />
                    <Typography variant="caption" color="text.secondary">Loss Price Bar</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#f59e0b', borderRadius: 0.5 }} />
                    <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 700 }}>
                      ⚡ Trailing SL Raised Bar
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 16, height: 3, bgcolor: '#dc2626' }} />
                    <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700 }}>
                      ── Stepped Trailing Stop Loss
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 16, height: 1, borderTop: '2px dashed #3b82f6' }} />
                    <Typography variant="caption" color="text.secondary">Entry Level</Typography>
                  </Box>
                </Box>

                {/* Composed Chart: Columns for Price + Stepped Line for Trailing Stop-Loss */}
                <Box sx={{ width: '100%', height: 340, bgcolor: '#fafafa', p: 1, borderRadius: 2, border: '1px solid #f1f5f9' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={points} margin={{ top: 15, right: 15, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis
                        dataKey="timeLabel"
                        tick={{ fontSize: 10, fill: '#64748b' }}
                        interval={Math.max(0, Math.floor(points.length / 8))}
                      />
                      <YAxis
                        domain={['auto', 'auto']}
                        tickFormatter={(v) => `₹${v.toFixed(0)}`}
                        tick={{ fontSize: 10, fill: '#64748b' }}
                        width={60}
                      />
                      <Tooltip content={<CustomTooltip buyPrice={buyPrice} />} />

                      {/* Entry Price Benchmark Line */}
                      <ReferenceLine
                        y={buyPrice}
                        stroke="#3b82f6"
                        strokeDasharray="4 4"
                        label={{ value: `Entry ₹${buyPrice}`, fill: '#3b82f6', fontSize: 10, position: 'insideTopLeft' }}
                      />

                      {/* Exit Price Benchmark Line (if sold) */}
                      {order.status === 'SOLD' && (
                        <ReferenceLine
                          y={exitPrice}
                          stroke="#10b981"
                          strokeDasharray="4 4"
                          label={{ value: `Exit ₹${exitPrice}`, fill: '#10b981', fontSize: 10, position: 'insideTopRight' }}
                        />
                      )}

                      {/* Price Columns */}
                      <Bar dataKey="price" name="Stock Price" radius={[3, 3, 0, 0]} maxBarSize={30}>
                        {points.map((p, idx) => (
                          <Cell
                            key={`cell-${idx}`}
                            fill={p.barColor || (p.price >= buyPrice ? '#10b981' : '#ef4444')}
                            stroke={p.isTrailing ? '#b45309' : undefined}
                            strokeWidth={p.isTrailing ? 1.5 : 0}
                          />
                        ))}
                      </Bar>

                      {/* Stepped Trailing Stop-Loss Line */}
                      <Line
                        type="stepAfter"
                        dataKey="sl"
                        name="Trailing Stop Loss"
                        stroke="#dc2626"
                        strokeWidth={2.5}
                        dot={<TrailingDot />}
                        activeDot={{ r: 6, fill: '#dc2626' }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}

            {/* Tab 1: Trailing Milestones Chronological Table */}
            {tabIndex === 1 && (
              <Box>
                {trailingEvents.length === 0 ? (
                  <Alert severity="info" sx={{ my: 2 }}>
                    No trailing stop-loss revisions were triggered during this trade duration. Stop-loss remained at initial {fmtMoney(order.initialSL)}.
                  </Alert>
                ) : (
                  <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc', '& th': { fontWeight: 700 } }}>
                          <TableCell>Time</TableCell>
                          <TableCell>Stock Price (LTP)</TableCell>
                          <TableCell>Previous SL</TableCell>
                          <TableCell>New Trailing SL</TableCell>
                          <TableCell>Gain Locked</TableCell>
                          <TableCell>Milestone / Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {trailingEvents.map((te, idx) => {
                          const oldSL = te.previousSL ?? te.previous_sl;
                          const newSL = te.newSL ?? te.new_sl;
                          const ltp = te.ltp ?? te.current_market_price;
                          const profitPct = te.profitPct ?? te.pnl_pct;
                          const reason = te.reason ?? te.sl_reason;

                          return (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {dayjs(te.timestamp).format('HH:mm:ss')}
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {dayjs(te.timestamp).format('DD MMM')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>
                                {fmtMoney(ltp)}
                              </TableCell>
                              <TableCell sx={{ color: '#64748b' }}>
                                {fmtMoney(oldSL)}
                              </TableCell>
                              <TableCell sx={{ color: '#dc2626', fontWeight: 800 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ArrowUpward sx={{ fontSize: 13, color: '#16a34a' }} />
                                  {fmtMoney(newSL)}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: '#16a34a', fontWeight: 700 }}>
                                +{profitPct}%
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={reason || `Phase ${te.phase || 1} Lock`}
                                  size="small"
                                  sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e', fontSize: 11 }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc' }}>
        <Button onClick={onClose} variant="contained" color="inherit" sx={{ fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
